import { makeAutoObservable, runInAction } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import {
  type OrganizationBusinessProfile,
  OrganizationTollFree,
  type TollFreeProfile,
} from 'entities/Compliance/model'
import { ComplianceApi, type IResponseOrganizationTollFreeUpdate } from 'entities/Compliance'
import { openIntercomNewMessage } from 'entities/Intercom'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { PresentationModeStore } from 'widgets/PresentationMode'
import { type ComplianceLayerStore } from 'pages/settings/pages/compliance/store/complianceLayerStore'
import { BusinessInfoStore } from 'pages/settings/pages/compliance/pages/tollFree/pages/BusinessInfo/store/businessInfoStore'
import { ContactInfoStore } from 'pages/settings/pages/compliance/pages/tollFree/pages/ContactInfo/store/contactInfoStore'
import { UseCaseStore } from 'pages/settings/pages/compliance/pages/tollFree/pages/UseCase/store/useCaseStore'
import { OptInMethodStore } from 'pages/settings/pages/compliance/pages/tollFree/pages/OptInMethod/store/optInMethodStore'
import { OptInConsentStore } from 'pages/settings/pages/compliance/pages/tollFree/pages/OptInConsent/store/optInConsentStore'
import { ForbiddenMessageStore } from 'pages/settings/pages/compliance/pages/tollFree/pages/ForbiddenMessageTopics/store/forbiddenMessageStore'
import { TollFreeRoutesSteps } from 'pages/settings/pages/compliance/pages/tollFree/route/type'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { UseCaseDetailsStore } from 'pages/settings/pages/compliance/pages/tollFree/pages/UseCaseDetails/store/UseCaseDetailsStore'
import { AddTollFreeNumberStore } from 'pages/settings/pages/compliance/pages/tollFree/pages/AddTollFreeNumber/store/addTollFreeNumberStore'
import { CompliancePath } from 'pages/settings/pages/compliance/route/compliancePath'
import { CommonNumbersStore } from 'pages/settings/pages/compliance/ui/CommonNumbers/store/commonNumbersStore'
import { TollFreeNavigationStore } from './tollFreeNavigationStore'
import { TollFreeAddNumberNavigationStore } from './tollFreeAddNumberNavigationStore'

export class TollFreeStepsStore {
  private _tollFreeProfile: TollFreeProfile | null = null
  private readonly _tollFreeNavigationStore: TollFreeNavigationStore
  private readonly _tollFreeAddNumberNavigationStore: TollFreeAddNumberNavigationStore
  private readonly _numbersStore: CommonNumbersStore
  private readonly _businessInfoStore: BusinessInfoStore
  private readonly _contactInfoStore: ContactInfoStore
  private readonly _useCaseStore: UseCaseStore
  private readonly _optInMethodStore: OptInMethodStore
  private readonly _optInConsentStore: OptInConsentStore
  private readonly _forbiddenMessageStore: ForbiddenMessageStore
  private readonly _useCaseDetailsStore: UseCaseDetailsStore
  private readonly _addTollFreeNumberStore: AddTollFreeNumberStore

  private _loading = true

  constructor(
    private _complianceLayerStore: ComplianceLayerStore,
    private _presentationStore: PresentationModeStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
    this._numbersStore = new CommonNumbersStore(
      {
        setLoading: this.setLoading,
        setPreAssignedNumber: this.complianceLayerStore.settingsStore.setTollFreePreAssignedNumber,
      },
      this._entities
    )
    this._businessInfoStore = new BusinessInfoStore(this)
    this._contactInfoStore = new ContactInfoStore(this)
    this._useCaseStore = new UseCaseStore(this)
    this._optInMethodStore = new OptInMethodStore(this, this._entities)
    this._optInConsentStore = new OptInConsentStore(this, this._presentationStore)
    this._tollFreeNavigationStore = new TollFreeNavigationStore(this)
    this._forbiddenMessageStore = new ForbiddenMessageStore(this, this._entities)
    this._useCaseDetailsStore = new UseCaseDetailsStore(this, this._entities)
    this._addTollFreeNumberStore = new AddTollFreeNumberStore(this, this._entities)
    this._tollFreeAddNumberNavigationStore = new TollFreeAddNumberNavigationStore()
  }

  init = async (id?: number, useCaseNumber?: string) => {
    runInAction(() => {
      this.setLoading(true)
    })

    if (id) {
      this.setTollFreeProfile(id, useCaseNumber)
      this.tollFreeNavigationStore.init(id)
    }

    this.setDataFromBusinessProfileRequest(this._complianceLayerStore.organizationBusinessProfile)

    runInAction(() => {
      this.setLoading(false)
    })
  }

  setLoading = (loading: boolean) => {
    this._loading = loading
  }

  setStepLoading = (value: boolean) => {
    this.stepsStore.setStepLoading(value)
  }

  setUseCaseData = () => {
    const { activeUseCaseNumber } = this.useCaseDetailsStore
    const individualData = activeUseCaseNumber?.individualTollFreeProfile

    const profileId = activeUseCaseNumber
      ? individualData?.use_case_id
      : this.tollFreeProfile?.use_case?.id

    if (!profileId) {
      return
    }

    const useCaseData: {
      id: number
      sampleMessage: string
      useCaseDesc: string
      monthlyVolume: number
      privacyPolicyUrl: string
      termsUrl: string
    } = {
      id: profileId,
      sampleMessage: activeUseCaseNumber
        ? individualData?.sample_message_1 || ''
        : this.tollFreeProfile?.use_case?.sample_message_1 || '',
      useCaseDesc: activeUseCaseNumber
        ? individualData?.use_case_description || ''
        : this.tollFreeProfile?.use_case?.description || '',
      monthlyVolume: activeUseCaseNumber
        ? individualData?.monthly_volume || 0
        : this.tollFreeProfile?.monthlyVolume || 0,
      privacyPolicyUrl: this.tollFreeProfile?.privacy_policy_url ?? '',
      termsUrl: this.tollFreeProfile?.terms_url ?? '',
    }

    this.useCaseStore.setUseCaseData(useCaseData)
  }

  setTollFreeData = (useCaseNumber?: string) => {
    const tollFreeProfile = this._tollFreeProfile

    if (useCaseNumber && !this.useCaseDetailsStore.activeUseCaseNumber) {
      // case for reload page when we lose _activeUseCaseNumber
      const item = tollFreeProfile?.numbers.find((num) => num.id === Number(useCaseNumber))

      item && this.useCaseDetailsStore.setActiveNumber(item)
    }

    if (!tollFreeProfile) return

    this.setUseCaseData()

    tollFreeProfile.numbers.forEach((num) => {
      if (num.id) {
        this.numbersStore.setMultipleItem({
          id: num.id,
          label: num.formattedNumber || '',
          data: {
            formatted_number: num.formattedNumber,
            numberable: {
              name: num.numberableName,
            },
            number: num.number,
            national_number: num.nationalNumber,
          },
        })
      }
    })

    const { activeUseCaseNumber } = this.useCaseDetailsStore
    const optInData =
      activeUseCaseNumber?.individualTollFreeProfile?.opt_in_data || tollFreeProfile.optInData

    this.optInConsentStore.setOptInData(optInData)
    this.optInMethodStore.setOptInMethodsFromTollFree(optInData || [])
  }

  setTollFreeProfile = (id: number, useCaseNumber?: string | number) => {
    const item = this._complianceLayerStore.organizationTollFree?.getItem(id)

    if (item) {
      this._tollFreeProfile = item
      this._tollFreeNavigationStore.setProfileId(id)
      this._tollFreeNavigationStore.setUseCaseNumber(useCaseNumber)
    }
  }

  get tollFreeProfile() {
    return this._tollFreeProfile
  }

  get loading() {
    return this._complianceLayerStore.loading || this._loading
  }

  get tollFreeNavigationStore() {
    return this._tollFreeNavigationStore
  }

  get numbersStore() {
    return this._numbersStore
  }

  get profileId() {
    return this._tollFreeNavigationStore.profileId
  }

  get complianceLayerStore() {
    return this._complianceLayerStore
  }

  get stepsStore() {
    return this._complianceLayerStore.stepsStore
  }

  get businessInfoStore() {
    return this._businessInfoStore
  }

  get contactInfoStore() {
    return this._contactInfoStore
  }

  get useCaseStore() {
    return this._useCaseStore
  }

  get optInMethodStore() {
    return this._optInMethodStore
  }

  get optInConsentStore() {
    return this._optInConsentStore
  }

  get forbiddenMessageStore() {
    return this._forbiddenMessageStore
  }

  get useCaseDetailsStore() {
    return this._useCaseDetailsStore
  }

  get addTollFreeNumberStore() {
    return this._addTollFreeNumberStore
  }

  get tollFreeAddNumberNavigationStore() {
    return this._tollFreeAddNumberNavigationStore
  }

  get stepLoading() {
    return this.stepsStore.stepLoading
  }

  get fieldsError() {
    return this.complianceLayerStore.errorStore.fieldsError
  }

  get fieldsErrorCommonText() {
    return this.complianceLayerStore.errorStore.fieldsErrorCommonText
  }

  deleteErrorValue = (keyName: string) => {
    this.complianceLayerStore.errorStore.deleteErrorValue(keyName)
  }

  setFieldsError = (fieldError: Record<string, string> | null) => {
    this.complianceLayerStore.errorStore.setFieldsError(fieldError)
  }

  setDataFromTollFreeRequest = (data: IResponseOrganizationTollFreeUpdate) => {
    const organizationTollFree = new OrganizationTollFree({
      data: [data.toll_free_profile],
    })

    this._entities.complianceStore.setOrganizationTollFree(organizationTollFree)

    this.complianceLayerStore.setBusinessInformation(data.business_information)
  }

  setDataFromBusinessProfileRequest = (
    organizationBusinessProfile?: OrganizationBusinessProfile | null
  ) => {
    const { businessInformation, pointsOfContact } = organizationBusinessProfile || {}

    if (businessInformation) {
      this._businessInfoStore.setCountryFromRegistrationTypeId(
        businessInformation.registration_type_id
      )

      this._businessInfoStore.setEinItem({
        city: businessInformation.city,
        ein: businessInformation.registration_number,
        ein_formatted: businessInformation.registration_number,
        name: businessInformation.business_name,
        state: businessInformation.state_id,
        street1: businessInformation.street_address_1,
        street2: businessInformation.street_address_2,
        zip: businessInformation.zip_code || businessInformation.postal_code,
      })
      this.businessInfoStore.setWebsiteUrl(businessInformation.website_url, true)
      businessInformation.country &&
        this.businessInfoStore.setActiveCountry(businessInformation.country)
      this._businessInfoStore.setEinSearch(businessInformation.registration_number)

      if (businessInformation.irs_ein_document) {
        this._businessInfoStore.irsEINDocumentUploaderStore.setIrsEinAttachment(
          businessInformation.irs_ein_document
        )
      }
    }

    if (pointsOfContact && pointsOfContact.firstContact) {
      this._contactInfoStore.setContactInfo(pointsOfContact.firstContact)
    }
  }

  closeSetting = async () => {
    await this._entities.complianceStore.fetchOrganizationTollFree()

    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.tollFree.root}`,
    })
  }

  updateStatusAction = (profile_id: number, numberId: number) => {
    modalStore.removeModal(this.useCaseDetailsStore.modalId)

    this._tollFreeNavigationStore.setCurrentStep(1)
    this._tollFreeNavigationStore.setUseCaseNumber(numberId)

    uiStore.changeRoute({
      path: `/${CompliancePath.compliance.tollFree.viewUseCaseSteps(profile_id, numberId)}/${
        TollFreeRoutesSteps.businessInfo
      }`,
    })
  }

  handleOpenIntercomWidget = () => {
    openIntercomNewMessage()
  }

  initNumbersStore = async () => {
    const assignedNumbers = this.complianceLayerStore.assignedTollFreeUnverifiedNumbers
    const unassignedNumbers = this.complianceLayerStore.unassignedTollFreeUnverifiedNumbers
    const { tollFreePreAssignedNumber } = this.complianceLayerStore.settingsStore

    this.numbersStore.init({
      assignedNumbers,
      unassignedNumbers,
      preAssignedNumber: tollFreePreAssignedNumber,
    })
  }

  numbersNextStepClick = async () => {
    const { multiSelectedKeys } = this.numbersStore

    try {
      this.setStepLoading(true)

      const { data } = await ComplianceApi.updateTollFreeNumbers(multiSelectedKeys, this.profileId)

      if (data) {
        await this._entities.complianceStore.loadAndUpdateUnverifiedTollFreeNumbers()
        uiStore.changeRoute({
          path: this.tollFreeNavigationStore.getNextUrl,
        })
        this.setDataFromTollFreeRequest(data)
      }

      if (!this.profileId) {
        this.setTollFreeProfile(
          data.toll_free_profile.id,
          this.useCaseDetailsStore.activeUseCaseNumber?.id
        )
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this.setStepLoading(false)
    }
  }

  submitForReviewClick = async (addToProfileId?: number) => {
    if (!this.profileId) return

    try {
      this.setStepLoading(true)

      const numbersForAdd = addToProfileId ? [addToProfileId] : this.numbersStore.multiSelectedKeys

      const oldNumbers = this._entities.complianceStore.organizationTollFree
        ?.getItem(this.profileId)
        ?.numbers.map((item) => item.id)

      const { data } = await ComplianceApi.submitTollFreeForReview(this.profileId, {
        numbers: [...(oldNumbers || []), ...numbersForAdd],

        additional_numbers: numbersForAdd,
      })
      this._entities.complianceStore.loadAndUpdateUnverifiedTollFreeNumbers()

      this._entities.complianceStore.organizationTollFree?.updateItem(data.data)

      const path = !this.addTollFreeNumberStore.returnToRootAfterAdd
        ? `/${CompliancePath.compliance.tollFree.view(this.profileId)}`
        : `/${SettingsPath.compliance.tollFree.root}`

      if (data) {
        uiStore.changeRoute({
          path,
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this.setStepLoading(false)
      this.addTollFreeNumberStore.setReturnToRootAfterAdd(true)
    }
  }

  dispose = () => {}
}
